import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, ArrowUpRight, Clapperboard, Film, Layers, Target, MonitorPlay, Wand2 } from "lucide-react";
import { fetchPackages } from "@/lib/api";
import { Reveal, Eyebrow, MaskedLines } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { PackageCard } from "@/components/PackageCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1759393852314-59dc00faeed3?auto=format&fit=crop&w=1920&q=80";

const STATS = [
  { value: "3", label: "Piattaforme: TikTok, IG, FB" },
  { value: "72h", label: "Consegna media" },
  { value: "10", label: "Pacchetti a catalogo" },
  { value: "79,99 €", label: "Per iniziare" },
];

const BENTO = [
  {
    icon: Target,
    title: "Strategia social",
    desc: "Audit dei canali, calendario editoriale e hook pensati per il tuo pubblico.",
    span: "md:col-span-4 row-span-2",
    image: "https://images.pexels.com/photos/6476790/pexels-photo-6476790.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    icon: Clapperboard,
    title: "Riprese verticali",
    desc: "Studio attrezzato per il formato 9:16: creator, luci e fondali su misura.",
    span: "md:col-span-8",
    image: "https://images.unsplash.com/photo-1719937051157-d3d81cc28e86?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: Wand2,
    title: "Editing & sottotitoli",
    desc: "Montaggio ritmato, sottotitoli animati, trending audio e sound design.",
    span: "md:col-span-5",
    image: "https://images.pexels.com/photos/1188751/pexels-photo-1188751.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    icon: MonitorPlay,
    title: "Ad ready",
    desc: "Master pronti per TikTok Ads, Meta Ads e Spark Ads, in ogni formato.",
    span: "md:col-span-3",
  },
];

const PROCESS = [
  { n: "01", title: "Briefing & audit canali", desc: "Analizziamo i tuoi profili, i competitor e il pubblico da raggiungere." },
  { n: "02", title: "Hook & storyboard", desc: "Scriviamo i primi 3 secondi e la struttura narrativa di ogni video." },
  { n: "03", title: "Riprese & editing", desc: "Giriamo in studio o presso di te, poi montaggio, grafiche e sottotitoli." },
  { n: "04", title: "Pubblicazione & ads", desc: "Master per ogni piattaforma, pronti per l'organico e le sponsorizzate." },
];

export const Home = () => {
  const [packages, setPackages] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    fetchPackages().then(setPackages).catch(() => {});
  }, []);

  const featured = packages.filter((p) => p.popular).slice(0, 3);

  return (
    <div>
      {/* ============ HERO ============ */}
      <section ref={heroRef} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
          <img src={HERO_IMG} alt="Produzione video social con smartphone su treppiede" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-ink/40" />

        <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-lineblue glass px-4 py-2"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-electric opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-electric" />
            </span>
            <span data-testid="hero-status-pill" className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-200">
              Studio video — Social & Advertising
            </span>
          </motion.div>

          <MaskedLines
            as="h1"
            delay={0.25}
            className="font-display font-bold tracking-tight text-white leading-[1.05] text-4xl sm:text-6xl lg:text-7xl max-w-5xl"
            lines={[
              "Video che fermano",
              "lo scroll su TikTok,",
              <span key="l3" className="text-gradient-blue">Instagram e Facebook.</span>,
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
          >
            Produzione video e advertising per i social. Dieci pacchetti a
            partire da 79,99 €: hook nei primi 3 secondi, montaggio verticale
            e sottotitoli pronti per TikTok, Instagram e Facebook.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/pacchetti"
              data-testid="hero-cta-explore"
              className="group inline-flex items-center gap-3 rounded-full bg-electric px-7 py-4 font-sans text-sm font-bold text-ink transition-all duration-300 hover:bg-glow hover:shadow-[0_0_40px_rgba(56,189,248,0.35)]"
            >
              Esplora i pacchetti
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/chi-siamo"
              data-testid="hero-cta-consultation"
              className="inline-flex items-center gap-3 rounded-full border border-glow/40 px-7 py-4 font-sans text-sm font-semibold text-white transition-all duration-300 hover:border-electric hover:text-electric"
            >
              Parla con noi
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-8 sm:grid-cols-4"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-3xl font-bold text-white">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          style={{ opacity: fade }}
          className="absolute bottom-8 right-8 z-10 hidden lg:flex flex-col items-center gap-3 text-slate-400"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <ArrowDown className="h-4 w-4" />
          </motion.span>
        </motion.div>
      </section>

      {/* ============ MARQUEE ============ */}
      <Marquee
        items={[
          "Video TikTok",
          "Instagram Reels",
          "Facebook Ads",
          "UGC Creation",
          "Sottotitoli Animati",
          "Campagne Sponsorizzate",
          "Montaggio Verticale",
        ]}
      />

      {/* ============ BENTO SERVIZI ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid gap-4 md:grid-cols-12">
          <Reveal className="md:col-span-4 flex flex-col justify-between">
            <Eyebrow>Cosa facciamo</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight">
              Un solo partner,
              <br />
              tutta la catena video.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">
              Dalla strategia alla post-produzione: un unico interlocutore
              senior per ogni progetto video della tua azienda.
            </p>
          </Reveal>

          {BENTO.map((b, i) => (
            <Reveal
              key={b.title}
              delay={0.08 * i}
              className={`${b.span} group relative overflow-hidden rounded-2xl border border-lineblue bg-panel p-7 transition-colors duration-500 hover:border-glow`}
            >
              {b.image && (
                <img
                  src={b.image}
                  alt={b.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-25 transition-all duration-700 group-hover:scale-105 group-hover:opacity-40"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-panel via-panel/70 to-panel/20" />
              <div className="relative z-10 flex h-full flex-col justify-end min-h-[220px]">
                <b.icon className="mb-4 h-7 w-7 text-electric" />
                <h3 className="font-display text-xl font-semibold text-white">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ PACCHETTI IN EVIDENZA ============ */}
      <section className="border-t border-lineblue bg-surface/40 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Eyebrow>Il catalogo</Eyebrow>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                Pacchetti in evidenza
              </h2>
            </div>
            <Link
              to="/pacchetti"
              data-testid="home-view-all-packages"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-electric"
            >
              Tutti i 10 pacchetti
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featured.length > 0
              ? featured.map((p, i) => <PackageCard key={p.slug} pkg={p} index={i} />)
              : [...Array(3)].map((_, i) => (
                  <div key={i} className="h-[420px] animate-pulse rounded-2xl border border-lineblue bg-panel" />
                ))}
          </div>
        </div>
      </section>

      {/* ============ PROCESSO ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <Reveal>
          <Eyebrow>Il metodo</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight">
            Quattro passi dal brief al master finale.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-lineblue bg-lineblue sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.08} className="bg-ink p-8 transition-colors duration-500 hover:bg-surface">
              <p className="font-display text-5xl font-bold text-cobalt/60">{step.n}</p>
              <h3 className="mt-6 font-display text-lg font-semibold text-white">{step.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ CTA FINALE ============ */}
      <section className="relative overflow-hidden border-t border-lineblue">
        <img
          src="https://images.pexels.com/photos/11025645/pexels-photo-11025645.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Suite di editing"
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/60 to-ink" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-28 text-center">
          <Reveal>
            <Film className="mx-auto h-10 w-10 text-electric" />
            <h2 className="mt-8 font-display text-3xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
              Pronto a fermare lo scroll?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-300">
              Scegli il pacchetto, aggiungilo al carrello e avvia il progetto
              in 72 ore. Pagamento sicuro con Stripe.
            </p>
            <Link
              to="/pacchetti"
              data-testid="home-final-cta"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-electric px-8 py-4 font-sans text-sm font-bold text-ink transition-all duration-300 hover:bg-glow hover:shadow-[0_0_40px_rgba(56,189,248,0.35)]"
            >
              Vai al catalogo
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
